import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { getFilterStore } from './useFilterStore';

type SearchStore = {
  basicSearchInput: string;
  /** Last natural-language query used for a successful AI search (animal tab). */
  aiSearchPrompt: string;
  openResult: boolean;
  openBasicSearchDialog: boolean;
  isAiMode: boolean;
  clearSearchResult: () => void;
  setBasicSearch: (term: string) => void;
  /** Updates the visible search string without recomputing flat filters (e.g. restoring AI prompt). */
  setSearchDisplayTerm: (term: string) => void;
  setAiSearchPrompt: (prompt: string) => void;
  setOpenResult: (value: boolean) => void;
  setOpenBasicSearchDialog: (value: boolean) => void;
  setIsAiMode: (value: boolean) => void;
};

const useSearchStore = create<SearchStore>()(
  persist(
    devtools(
      immer((set) => ({
        basicSearchInput: '',
        aiSearchPrompt: '',
        openResult: false,
        openBasicSearchDialog: false,
        isAiMode: false,

        setBasicSearch: (searchTerm) =>
          set((_state) => {
            const animalStore = getFilterStore('animal');
            const memberStore = getFilterStore('members');
            const saleCatalogueStore = getFilterStore('sale-catalogues');
            const saleCatalogueDetailAnimalsStore = getFilterStore(
              'sale-catalogues-detail-animals',
            );
            const semenCatalogueDetailAnimalsStore = getFilterStore(
              'semen-catalogues-detail-animals',
            );

            animalStore.getState().computeFiltersFromSearchTerm(searchTerm);
            memberStore.getState().computeFiltersFromSearchTerm(searchTerm);
            saleCatalogueStore
              .getState()
              .computeFiltersFromSearchTerm(searchTerm);
            saleCatalogueDetailAnimalsStore
              .getState()
              .computeFiltersFromSearchTerm(searchTerm);
            semenCatalogueDetailAnimalsStore
              .getState()
              .computeFiltersFromSearchTerm(searchTerm);

            return {
              basicSearchInput: searchTerm,
            };
          }),

        setSearchDisplayTerm: (term) =>
          set(() => ({
            basicSearchInput: term,
          })),

        setAiSearchPrompt: (prompt) =>
          set((_state) => ({
            aiSearchPrompt: prompt,
          })),

        setOpenResult: (value) =>
          set((_state) => ({
            openResult: value,
          })),

        setOpenBasicSearchDialog: (value) =>
          set((_state) => ({
            openBasicSearchDialog: value,
          })),

        setIsAiMode: (value) =>
          set(() => ({
            isAiMode: value,
          })),

        clearSearchResult: () =>
          set((_state) => ({
            openResult: false,
          })),
      })),
      { name: 'SearchStore' },
    ),
    {
      name: 'SearchStore',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

export default useSearchStore;
